// Guided demo in the simulator: drives the real chat UI step by step, at a human pace, with captions.
import { simSeedCheckin } from "../live/engagementApi";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const readTime = (caption = "", sub = "") => Math.max(3200, (caption.length + sub.length) * 55);

function find(target) {
  if (target.testid) {
    const all = document.querySelectorAll(`[data-testid="${target.testid}"]`);
    return all[all.length - 1] || null;
  }
  if (target.qr) {
    return [...document.querySelectorAll('[data-testid^="lab-qr-"]')].find((el) => el.innerText.trim().startsWith(target.qr)) || null;
  }
  return null;
}

async function waitFor(target, ms = 20000, control) {
  const end = Date.now() + ms;
  while (Date.now() < end) {
    if (control.stopped) return null;
    const el = find(target);
    if (el && !el.disabled) return el;
    await sleep(250);
  }
  return null;
}

async function spotlight(el, ms) {
  el.scrollIntoView({ block: "center", behavior: "smooth" });
  el.classList.add("demo-spotlight");
  await sleep(ms);
  el.classList.remove("demo-spotlight");
}

function setValue(el, value) {
  const proto = el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value").set.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

/** Runs steps; control = { stopped, paused, speed, onStep(index, step) }. */
export async function runDemo(steps, control) {
  for (let index = 0; index < steps.length; index += 1) {
    if (control.stopped) return false;
    while (control.paused && !control.stopped) await sleep(200);
    const step = steps[index];
    control.onStep(index, step);
    if (step.run) await step.run();
    const read = (step.read ?? readTime(step.caption, step.sub)) / control.speed;
    if (step.click || step.type) {
      const target = step.click || step.type;
      const el = await waitFor(target, step.timeout || 20000, control);
      if (!el) {
        control.onStep(index, { ...step, caption: "Étape introuvable", sub: "Le flow a changé : la démo s’arrête ici." });
        return false;
      }
      await sleep(read * 0.55);
      if (step.type) {
        el.focus();
        for (let i = 1; i <= step.text.length; i += 1) {
          if (control.stopped) return false;
          setValue(el, step.text.slice(0, i));
          await sleep(45 / control.speed);
        }
        await sleep(read * 0.45);
      } else {
        await spotlight(el, 1300 / control.speed);
        el.click();
        await sleep(read * 0.45);
      }
    } else {
      await sleep(read);
    }
    if (step.until) {
      for (let i = 0; i < 4 && !find(step.until.done); i += 1) {
        const next = await waitFor(step.until.next, 5000, control);
        if (!next) break;
        await spotlight(next, 900 / control.speed);
        next.click();
        await sleep(1600 / control.speed);
      }
    }
  }
  return true;
}

export const DEMOS = [
  {
    flowId: 1,
    title: "Flow 1 — Onboarding parent",
    steps: (play) => [
      { caption: "Flow 1 — Onboarding parent", sub: "Sophie ouvre Plan Étude pour la première fois. Tout se fait avec des boutons.", run: () => play(1), read: 4500 },
      { caption: "Plan Étude se présente", sub: "Un message court explique ce que l’application fait pour la famille." },
      { caption: "Sophie a compris le principe", sub: "Elle appuie sur « Compris ».", click: { qr: "Compris" } },
      { caption: "Émile est déjà inscrit", sub: "On passe directement à son premier plan d’étude.", read: 4200 },
      { caption: "Proposer un premier plan", sub: "Sophie choisit de le faire tout de suite.", click: { qr: "Proposer maintenant" } },
      { caption: "Quoi pratiquer ?", sub: "Sophie écrit ce qu’Émile doit travailler.", type: { testid: "plan-form-title" }, text: "Maths — exercices chapitre 3" },
      { caption: "Combien et quand ?", sub: "Nombre de séances par semaine, durée et échéance, puis le récapitulatif.", click: { testid: "plan-form-next" }, until: { next: { testid: "plan-form-next" }, done: { testid: "plan-form-submit" } } },
      { caption: "Envoyer à Émile", sub: "Le plan part en attente : Émile devra l’accepter (ou demander un changement).", click: { testid: "plan-form-submit" } },
      { caption: "Comment se passe le suivi", sub: "Plan Étude explique à Sophie ce qu’elle verra ensuite.", read: 4500 },
      { caption: "Sophie arrive sur son accueil", sub: "C’est le Flow 13 : elle y reviendra à chaque visite.", click: { qr: "C’est compris" } },
      { caption: "Fin du Flow 1", sub: "Prochain flow de la boucle : l’onboarding d’Émile (Flow 2).", read: 4000 },
    ],
  },
  {
    flowId: 24,
    title: "Flow 24 — Check-in de l’élève",
    steps: (play) => [
      { caption: "Flow 24 — Check-in de l’élève", sub: "Émile ouvre Plan Étude : en quelques secondes, il voit où il en est.", run: () => { simSeedCheckin("Émile"); play(24); }, read: 4200 },
      { caption: "Ce qui s’en vient", sub: "Les 3 prochaines échéances, de la plus proche à la plus lointaine.", read: 3000 },
      { caption: "Ce que tu as à faire", sub: "Ses plans d’étude en cours et les séances faites cette semaine.", read: 2700 },
      { caption: "Ce qui est prévu", sub: "Les séances déjà placées dans son horaire.", read: 2700 },
      { caption: "Un début de tâche", sub: "Plan Étude propose la prochaine séance prévue. Émile la commence.", click: { qr: "Commencer maintenant" } },
      { caption: "La séance commence", sub: "20 minutes. Il indiquera ensuite ce qu’il a fait.", read: 4500 },
    ],
  },
];
